import { useState, useMemo } from "react";
import { ShieldCheck, Plus, Trash2 } from "lucide-react";
import type { Policy, PolicyAdditionalInsured } from "@shared/schema";
import { normalizeInsuredName } from "@shared/normalize";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  useAdditionalInsureds,
  useDeleteAdditionalInsured,
  type InsuredAdditionalInsuredLink,
} from "@/lib/api";
import { AdditionalInsuredDialog } from "./AdditionalInsuredDialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/formatters";
import { ADDITIONAL_INSURED_TABLE_COLUMN_WIDTHS as COLUMN_WIDTHS } from "./asset-helpers";
import { AssetStatusBadge } from "./AssetStatusBadge";

const HEAD_CLASS = "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
const CELL_CLASS = "px-6 py-4";
const ACTIONS_CELL_CLASS = "py-4 pr-6 text-right";

export interface MergedAdditionalInsured {
  source: "crud" | "policy";
  name: string;
  email: string | null;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  coiCount: number;
  lastSentAt: string | null;
  createdAt: string | null;
  /** Missing address or has no coverages selected — the user must complete the record before COIs can be issued. */
  pending: boolean;
  /** Carrier-supplied with full address but not yet promoted to a CRUD entry (CSR-743). */
  imported: boolean;
  /**
   * CSR-2274 — a positive claim: a carrier snapshot exists for every eligible
   * policy and none of them names this holder. Rows sourced from the carrier
   * scrape itself (`source: "policy"`) can never be inactive by construction —
   * they exist because the carrier listed them.
   */
  inactive: boolean;
  crudLink?: InsuredAdditionalInsuredLink;
}

interface Props {
  insuredId: string;
  variant?: "policy" | "insured";
  policyAdditionalInsureds?: PolicyAdditionalInsured[] | null;
  policies?: Policy[];
  // CSR-1683 — when set, narrow crudItems (CRUD-managed, insured-scoped) to
  // links whose coverageIds reference this policy (composite id format
  // `<policyId>:<coverageKey>`). Mirrors LossPayeesSection/
  // WaiversOfSubrogationSection. policyItems (policyAdditionalInsureds) are
  // already scoped to the single policy being viewed and must NOT be filtered.
  policyId?: string;
}

function hasFullAddress(item: {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): boolean {
  return !!(item.street && item.city && item.state && item.zip);
}

export function AdditionalInsuredsSection({
  insuredId,
  variant = "insured",
  policyAdditionalInsureds,
  policies = [],
  policyId,
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const { data: links, isLoading } = useAdditionalInsureds(insuredId);
  const deleteMutation = useDeleteAdditionalInsured(insuredId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<InsuredAdditionalInsuredLink | null>(null);
  const [prefillName, setPrefillName] = useState<string | null>(null);
  const [prefillData, setPrefillData] = useState<PolicyAdditionalInsured | null>(null);

  const allCrudItems = links ?? [];
  const policyItems = policyAdditionalInsureds ?? [];

  // CSR-1683 — crudItems come from useAdditionalInsureds, which is
  // insured-scoped, not policy-scoped. Without this filter, an insured's
  // additional insureds all leak onto EVERY one of that insured's policies'
  // Assets tabs. Mirrors LossPayeesSection's filteredItems.
  //
  // CSR-1622 materializes CRUD links from the carrier scrape before the
  // CSR-1593 coverage sync backfills coverageIds (a sync that is deliberately
  // incomplete), so a coverage-only filter would hide a real, already-linked
  // record. Keep it if it also matches this policy's scraped snapshot by
  // name, mirroring WaiversOfSubrogationSection's scrapedNames fallback.
  const crudItems = useMemo(() => {
    if (!policyId) return allCrudItems;
    const scrapedNames = new Set(
      policyItems.map((item) => normalizeInsuredName(item.name ?? "")).filter(Boolean),
    );
    return allCrudItems.filter(
      (link) =>
        (link.coverageIds ?? []).some((cid) => cid.startsWith(`${policyId}:`)) ||
        scrapedNames.has(normalizeInsuredName(link.name ?? "")),
    );
  }, [allCrudItems, policyId, policyItems]);

  const merged = useMemo<MergedAdditionalInsured[]>(() => {
    const result: MergedAdditionalInsured[] = [];
    // Same normalization as the server-side dedup (lower + trim + collapsed
    // whitespace) — the scraped snapshot often spells a linked name in a
    // different case ("MONTWAY LLC" vs "Montway LLC"), and that must not
    // render the company twice.
    const seen = new Set<string>();

    for (const link of crudItems) {
      if (!link.name) continue;
      seen.add(normalizeInsuredName(link.name));
      const complete = hasFullAddress(link);
      const hasCoverages = Array.isArray(link.coverageIds) && link.coverageIds.length > 0;
      result.push({
        source: "crud",
        name: link.name,
        email: link.email,
        street: link.street ?? undefined,
        city: link.city ?? undefined,
        state: link.state ?? undefined,
        zip: link.zip ?? undefined,
        coiCount: link.coiCount,
        lastSentAt: link.lastSentAt,
        createdAt: link.createdAt,
        pending: !complete || !hasCoverages,
        imported: false,
        inactive: !!link.inactive,
        crudLink: link,
      });
    }

    for (const item of policyItems) {
      const normName = normalizeInsuredName(item.name ?? "");
      if (!normName || seen.has(normName)) continue;
      seen.add(normName);
      const complete = hasFullAddress(item);
      result.push({
        source: "policy",
        name: item.name,
        email: null,
        street: item.street ?? undefined,
        city: item.city ?? undefined,
        state: item.state ?? undefined,
        zip: item.zip ?? undefined,
        coiCount: 0,
        lastSentAt: null,
        createdAt: null,
        pending: !complete,
        imported: complete,
        // CSR-2274 — a scraped snapshot row exists only because the carrier
        // listed this holder, so it can never be inactive by construction.
        inactive: false,
      });
    }

    // CSR-2274 — Active holders first, Inactive after; the sort is stable, so
    // each group keeps the order above.
    return result.sort((a, b) => Number(a.inactive) - Number(b.inactive));
  }, [crudItems, policyItems]);

  const handleDelete = async (e: React.MouseEvent, link: InsuredAdditionalInsuredLink) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Remove Additional Insured",
      description: `Remove "${link.name}" as an additional insured?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(link.linkId);
      toast({ title: "Deleted", description: "Additional insured removed." });
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleRowClick = (item: MergedAdditionalInsured) => {
    if (item.crudLink) {
      setEditingLink(item.crudLink);
      setPrefillName(null);
      setPrefillData(null);
    } else if (item.imported) {
      // Carrier-supplied with full address — pre-populate the dialog so the
      // user just clicks "Add" to promote it to a managed CRUD entry (CSR-743).
      setEditingLink(null);
      setPrefillName(null);
      setPrefillData({
        name: item.name,
        street: item.street,
        city: item.city,
        state: item.state,
        zip: item.zip,
      });
    } else {
      setEditingLink(null);
      setPrefillName(item.name);
      setPrefillData(null);
    }
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingLink(null);
    setPrefillName(null);
    setPrefillData(null);
    setDialogOpen(true);
  };

  const addButton = (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      onClick={handleAdd}
      data-testid="additional-insureds-add"
    >
      <Plus className="w-4 h-4" /> Add
    </Button>
  );

  const header = (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-foreground" aria-hidden="true" />
        <h3 className="text-base font-semibold">Additional Insureds</h3>
      </div>
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-muted text-xs font-medium text-muted-foreground"
          data-testid="additional-insureds-count"
        >
          {merged.length}
        </span>
        {addButton}
      </div>
    </div>
  );

  const tableContent = (
    <>
      {isLoading ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      ) : merged.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          No additional insureds linked.
        </div>
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[0]}`}>Name</TableHead>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[1]}`}>Email</TableHead>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[2]}`}>COIs Sent</TableHead>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[3]}`}>Last Sent</TableHead>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[4]}`}>Created At</TableHead>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[5]}`}>Status</TableHead>
              <TableHead className={`${HEAD_CLASS} ${COLUMN_WIDTHS[6]}`}>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {merged.map((item, idx) => (
              <TableRow
                key={item.crudLink?.linkId ?? `policy-${idx}`}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(item)}
              >
                <TableCell className={`${CELL_CLASS} font-medium`}>
                  <div>{item.name}</div>
                  {item.imported || (item.source === "crud" && hasFullAddress(item)) ? (
                    <div className="text-xs font-normal text-muted-foreground mt-0.5">
                      {item.street}, {item.city}, {item.state} {item.zip}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {item.pending ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase bg-amber-500/10 text-amber-700">
                      Pending Additional Information
                    </span>
                  ) : item.imported ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase bg-sky-500/10 text-sky-700">
                      Imported from carrier
                    </span>
                  ) : (
                    item.email || "—"
                  )}
                </TableCell>
                <TableCell className={CELL_CLASS}>
                  {item.pending || item.imported ? "—" : (
                    <Badge variant="outline" className="rounded-full">
                      {item.coiCount}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {item.pending || item.imported
                    ? "—"
                    : item.lastSentAt
                      ? formatDate(item.lastSentAt)
                      : "Never"}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {item.createdAt ? formatDate(item.createdAt) : "—"}
                </TableCell>
                <TableCell className={CELL_CLASS}>
                  <AssetStatusBadge status={item.inactive ? "inactive" : "active"} />
                </TableCell>
                <TableCell className={ACTIONS_CELL_CLASS}>
                  {item.crudLink ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={(e) => handleDelete(e, item.crudLink!)}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );

  const dialog = (
    <AdditionalInsuredDialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setEditingLink(null);
          setPrefillName(null);
          setPrefillData(null);
        }
      }}
      insuredId={insuredId}
      policies={policies}
      editData={editingLink}
      prefillName={prefillName}
      prefillData={prefillData}
    />
  );

  if (variant === "insured") {
    return (
      <>
        <Card className="overflow-hidden" data-testid="additional-insureds-section">
          {header}
          {tableContent}
        </Card>
        {dialog}
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        data-testid="policy-additional-insureds"
      >
        {header}
        {tableContent}
      </section>
      {dialog}
      {confirmDialog}
    </>
  );
}
